import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Radio,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { PromotionsService } from '../../services/promotions.service';
import { useCategoryLabelMaps } from '../../hooks/useCategoryLabelMaps';
import { formatCategoryLabel } from '../../utils/categoryLabels';

interface Vendor {
  vendorId: string;
  companyName?: string;
  contactName?: string;
  email?: string;
  profileImageUrl?: string;
  rating?: number;
  status?: string;
  services?: Array<{
    type: string;
    items: string[];
  }>;
}

interface AddVendorModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (vendorId: string) => Promise<void>;
  excludeVendorIds?: string[];
}

// Cache key for localStorage
const VENDORS_CACHE_KEY = 'eventi_active_vendors_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface VendorCache {
  vendors: Vendor[];
  timestamp: number;
}

const AddVendorModal: React.FC<AddVendorModalProps> = ({
  open,
  onClose,
  onAdd,
  excludeVendorIds = [],
}) => {
  const { maps: categoryLabels } = useCategoryLabelMaps();
  const [searchQuery, setSearchQuery] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [allVendors, setAllVendors] = useState<Vendor[]>([]); // Store all vendors for filtering
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Load vendors from cache
  const loadFromCache = (): Vendor[] | null => {
    try {
      const cached = localStorage.getItem(VENDORS_CACHE_KEY);
      if (!cached) return null;

      const cacheData: VendorCache = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid
      if (now - cacheData.timestamp < CACHE_DURATION) {
        console.log('📦 Loading vendors from cache');
        return cacheData.vendors;
      } else {
        console.log('⏰ Cache expired, will fetch fresh data');
        localStorage.removeItem(VENDORS_CACHE_KEY);
        return null;
      }
    } catch (err) {
      console.error('Error loading from cache:', err);
      return null;
    }
  };

  // Save vendors to cache
  const saveToCache = (vendors: Vendor[]) => {
    try {
      const cacheData: VendorCache = {
        vendors,
        timestamp: Date.now(),
      };
      localStorage.setItem(VENDORS_CACHE_KEY, JSON.stringify(cacheData));
      console.log('💾 Vendors saved to cache');
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  };

  // Fetch active vendors
  const fetchVendors = async () => {
    // Try to load from cache first
    const cachedVendors = loadFromCache();
    if (cachedVendors) {
      setAllVendors(cachedVendors);
      // Filter out already promoted vendors
      const availableVendors = cachedVendors.filter(
        (v: Vendor) => !excludeVendorIds.includes(v.vendorId)
      );
      setVendors(availableVendors);
      return;
    }

    // If no cache, fetch from API
    setLoading(true);
    setError(null);

    try {
      const result = await PromotionsService.searchActiveVendors('', 1, 100); // Fetch more vendors
      
      // Save to cache
      saveToCache(result.vendors);
      setAllVendors(result.vendors);
      
      // Filter out already promoted vendors
      const availableVendors = result.vendors.filter(
        (v: Vendor) => !excludeVendorIds.includes(v.vendorId)
      );
      
      setVendors(availableVendors);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch vendors';
      setError(errorMessage);
      console.error('Error fetching vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter vendors based on search query
  const filterVendors = (query: string) => {
    if (!query) {
      // Show all available vendors (excluding promoted ones)
      const availableVendors = allVendors.filter(
        (v: Vendor) => !excludeVendorIds.includes(v.vendorId)
      );
      setVendors(availableVendors);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = allVendors.filter((vendor: Vendor) => {
      // Exclude already promoted vendors
      if (excludeVendorIds.includes(vendor.vendorId)) return false;
      
      // Extract category and services for search (resolve type keys to names)
      const category = formatCategoryLabel(
        vendor.services?.[0]?.type || '',
        categoryLabels?.typeByKey || {},
      );
      const services = vendor.services?.map(s => s.items?.join(', ')).filter(Boolean).join(' • ') || '';
      
      // Filter by search query - check all relevant fields
      const companyNameMatch = vendor.companyName?.toLowerCase().includes(lowerQuery);
      const contactNameMatch = vendor.contactName?.toLowerCase().includes(lowerQuery);
      const emailMatch = vendor.email?.toLowerCase().includes(lowerQuery);
      const categoryMatch = category?.toLowerCase().includes(lowerQuery);
      const servicesMatch = services?.toLowerCase().includes(lowerQuery);
      
      return companyNameMatch || contactNameMatch || emailMatch || categoryMatch || servicesMatch;
    });
    
    setVendors(filtered);
  };

  // Initial fetch when modal opens
  useEffect(() => {
    if (open) {
      fetchVendors();
      setSelectedVendorId(null);
      setSearchQuery('');
    }
  }, [open, excludeVendorIds]);

  // Filter vendors when search query changes
  useEffect(() => {
    if (open && allVendors.length > 0) {
      filterVendors(searchQuery);
    }
  }, [searchQuery, allVendors, excludeVendorIds]);

  // Handle add vendor
  const handleAdd = async () => {
    if (!selectedVendorId) return;

    setAdding(true);
    try {
      await onAdd(selectedVendorId);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderRadius: 'var(--palette-light-4-value)',
          padding: 'var(--palette-light-4-value)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 'var(--palette-light-4-value) var(--palette-light-6-value)',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--palette-light-font-size-h3-value)',
            fontWeight: 700,
            color: 'var(--palette-light-text-primary-value-hex)',
          }}
        >
          Add Vendor to Promotions
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 'var(--palette-light-6-value)' }}>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search for active vendors"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            marginBottom: 'var(--palette-light-4-value)',
            '& .MuiOutlinedInput-root': {
              height: '48px',
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: 'var(--palette-light-3-value)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 'var(--palette-light-10-value)' }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ marginBottom: 'var(--palette-light-4-value)' }}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!loading && !error && vendors.length === 0 && (
          <Box sx={{ padding: 'var(--palette-light-10-value)', textAlign: 'center' }}>
            <Typography sx={{ color: 'var(--palette-light-text-secondary-value-hex)' }}>
              {searchQuery ? 'No active vendors match your search' : 'No active vendors available'}
            </Typography>
          </Box>
        )}

        {/* Vendors List */}
        {!loading && !error && vendors.length > 0 && (
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                borderRadius: '4px',
              },
            }}
          >
            {vendors.map((vendor) => {
              // Extract category and services — show type names, not catalog keys
              const category = formatCategoryLabel(
                vendor.services?.[0]?.type || '',
                categoryLabels?.typeByKey || {},
              );
              const services = vendor.services?.map(s => s.items?.join(', ')).filter(Boolean).join(' • ') || '';
              
              return (
                <Box
                  key={vendor.vendorId}
                  onClick={() => setSelectedVendorId(vendor.vendorId)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--palette-light-4-value)',
                    padding: 'var(--palette-light-3-value)',
                    borderRadius: 'var(--palette-light-2-value)',
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor:
                      selectedVendorId === vendor.vendorId
                        ? 'var(--palette-light-brand-primary-value-hex)'
                        : 'transparent',
                    backgroundColor:
                      selectedVendorId === vendor.vendorId
                        ? 'var(--palette-light-brand-secondary-value-hex)'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    },
                    marginBottom: 'var(--palette-light-2-value)',
                  }}
                >
                  <Radio
                    checked={selectedVendorId === vendor.vendorId}
                    sx={{
                      padding: 0,
                      color: 'var(--palette-light-text-secondary-value-hex)',
                      '&.Mui-checked': {
                        color: 'var(--palette-light-brand-primary-value-hex)',
                      },
                    }}
                  />

                  {vendor.profileImageUrl && (
                    <Box
                      component="img"
                      src={vendor.profileImageUrl}
                      alt={vendor.companyName || vendor.vendorId}
                      sx={{
                        width: '160px',
                        height: '81.45px',
                        borderRadius: 'var(--palette-light-2-value)',
                        objectFit: 'cover',
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}

                  {!vendor.profileImageUrl && (
                    <Box
                      sx={{
                        width: '160px',
                        height: '81.45px',
                        borderRadius: 'var(--palette-light-2-value)',
                        backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: 'var(--palette-light-text-tertiary-value-hex)', fontSize: '12px' }}>
                        No Image
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)' }}>
                    {/* Vendor Name */}
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        fontWeight: 700,
                        color: 'var(--palette-light-text-primary-value-hex)',
                      }}
                    >
                      {vendor.companyName || vendor.contactName || vendor.vendorId}
                    </Typography>
                    
                    {/* Vendor Email */}
                    {vendor.email && (
                      <Typography
                        sx={{
                          fontSize: 'var(--palette-light-font-size-small-value)',
                          color: 'var(--palette-light-text-secondary-value-hex)',
                        }}
                      >
                        {vendor.email}
                      </Typography>
                    )}
                    
                    {/* Category and Services */}
                    {(category || services) && (
                      <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)', alignItems: 'center', flexWrap: 'wrap' }}>
                        {category && (
                          <Typography
                            sx={{
                              fontSize: 'var(--palette-light-font-size-small-value)',
                              color: 'var(--palette-light-text-primary-value-hex)',
                            }}
                          >
                            {category}
                          </Typography>
                        )}
                        {category && services && (
                          <Typography
                            sx={{
                              fontSize: 'var(--palette-light-font-size-small-value)',
                              color: 'var(--palette-light-text-primary-value-hex)',
                            }}
                          >
                            •
                          </Typography>
                        )}
                        {services && (
                          <Typography
                            sx={{
                              fontSize: 'var(--palette-light-font-size-small-value)',
                              color: 'var(--palette-light-text-primary-value-hex)',
                            }}
                          >
                            {services}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: 'var(--palette-light-4-value) var(--palette-light-6-value)',
          gap: 'var(--palette-light-3-value)',
        }}
      >
        <Button
          onClick={onClose}
          disabled={adding}
          sx={{
            textTransform: 'none',
            color: 'var(--palette-light-text-primary-value-hex)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          disabled={!selectedVendorId || adding}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
            color: 'var(--palette-light-text-foreground-value-hex)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: 'var(--palette-light-background-tertiary-value-hex)',
              color: 'var(--palette-light-text-tertiary-value-hex)',
            },
          }}
        >
          {adding ? <CircularProgress size={20} /> : 'Add to Promotions'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVendorModal;
